import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import mermaid from 'mermaid';

let mermaidInitialized = false;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.15;

const initMermaid = () => {
  if (mermaidInitialized) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'neutral',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: true, htmlLabels: true },
    sequence: { useMaxWidth: true, wrap: true },
    block: { useMaxWidth: true },
  });
  mermaidInitialized = true;
};

const measureSvg = (host) => {
  const svg = host?.querySelector('svg');
  if (!svg) return null;

  const vb = svg.viewBox?.baseVal;
  if (vb?.width > 0 && vb?.height > 0) {
    svg.setAttribute('viewBox', `${vb.x} ${vb.y} ${vb.width} ${vb.height}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  svg.removeAttribute('width');
  svg.removeAttribute('height');

  const naturalWidth = Math.max(vb?.width || 640, 1);
  const naturalHeight = Math.max(vb?.height || 240, 1);
  const containerWidth = Math.max(host.parentElement?.clientWidth || naturalWidth, 1);

  let displayWidth = naturalWidth;
  let displayHeight = naturalHeight;

  if (naturalWidth > containerWidth) {
    displayWidth = containerWidth;
    displayHeight = (containerWidth / naturalWidth) * naturalHeight;
  }

  svg.style.display = 'block';
  svg.style.width = `${displayWidth}px`;
  svg.style.height = `${displayHeight}px`;
  svg.style.maxWidth = '100%';

  return { naturalWidth, naturalHeight, displayWidth, displayHeight, containerWidth };
};

const applyZoomToSvg = (host, baseSize, zoom) => {
  const svg = host?.querySelector('svg');
  if (!svg || !baseSize) return;

  const nextWidth = Math.max(baseSize.displayWidth * zoom, 1);
  const nextHeight = Math.max(baseSize.displayHeight * zoom, 1);

  svg.style.width = `${nextWidth}px`;
  svg.style.height = `${nextHeight}px`;
  svg.style.maxWidth = zoom > 1 ? 'none' : '100%';
};

const MermaidBlock = ({ chart, id }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [diagramSize, setDiagramSize] = useState(null);
  const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);

  useEffect(() => {
    initMermaid();
    let cancelled = false;

    const renderChart = async () => {
      if (!containerRef.current || !chart) return;

      try {
        const renderId = `mermaid-${id}-${Date.now()}`;
        const { svg } = await mermaid.render(renderId, chart);
        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = svg;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (cancelled || !containerRef.current) return;
            const dims = measureSvg(containerRef.current);
            if (dims) {
              setDiagramSize(dims);
              applyZoomToSvg(containerRef.current, dims, 1);
            }
            setError(null);
          });
        });
      } catch (renderError) {
        if (!cancelled) {
          setDiagramSize(null);
          setError(renderError.message || 'Error al renderizar diagrama');
        }
      }
    };

    renderChart();

    return () => {
      cancelled = true;
    };
  }, [chart, id]);

  useEffect(() => {
    if (!containerRef.current || !diagramSize) return;
    applyZoomToSvg(containerRef.current, diagramSize, zoom);

    const viewportWidth = viewportRef.current?.clientWidth || diagramSize.containerWidth;
    setNeedsHorizontalScroll(diagramSize.displayWidth * zoom > viewportWidth + 1);
  }, [zoom, diagramSize]);

  const decreaseZoom = () => {
    setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
  };

  const increaseZoom = () => {
    setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
  };

  const resetZoom = () => setZoom(1);

  const scaledWidth = diagramSize ? Math.ceil(diagramSize.displayWidth * zoom) : undefined;
  const scaledHeight = diagramSize ? Math.ceil(diagramSize.displayHeight * zoom) : undefined;

  if (error) {
    return (
      <pre className="docs-mermaid-error">
        {error}
        {'\n'}
        {chart}
      </pre>
    );
  }

  return (
    <div className="docs-mermaid-wrapper">
      <div className="docs-mermaid-toolbar docs-no-print">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={decreaseZoom} title={t('docs.diagram.zoomOut')}>
          <i className="fa fa-minus" />
        </button>
        <span className="docs-mermaid-zoom-label">{Math.round(zoom * 100)}%</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={increaseZoom} title={t('docs.diagram.zoomIn')}>
          <i className="fa fa-plus" />
        </button>
        <button type="button" className="btn btn-sm btn-link docs-mermaid-reset" onClick={resetZoom}>
          {t('docs.diagram.zoomReset')}
        </button>
      </div>
      <div
        ref={viewportRef}
        className={`docs-mermaid-viewport ${needsHorizontalScroll ? 'docs-mermaid-viewport--scroll-x' : ''}`}
        style={{
          minHeight: scaledHeight ? `${scaledHeight + 24}px` : undefined,
        }}
      >
        <div
          className="docs-mermaid-svg-host"
          ref={containerRef}
          style={{
            width: scaledWidth ? `${scaledWidth}px` : '100%',
            minHeight: scaledHeight ? `${scaledHeight}px` : undefined,
          }}
        />
      </div>
    </div>
  );
};

export default MermaidBlock;
